import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectedClientSelector } from '../../store/slices/selectedClient';
import { useSidebar } from '../../contexts/SidebarContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Globe,
  MapPin,
  Phone,
  Check,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  X,
  Menu,
  AlertTriangle,
  Search,
  Upload,
  Facebook
} from 'lucide-react';
import {
  getListingProfile,
  updateListingProfile,
  getListingAuditResults,
  triggerListingAudit,
  triggerListingSync,
  getFoursquareConfig,
  exchangeFoursquareCode,
  saveFoursquareVenue
} from '../../services/listingApi';
import axios from 'axios';
import Swal from 'sweetalert2';
import ReactSelect from 'react-select';
import { countries, usStatesAndCaProvinces } from '../../utils/locationData';
import { API_CLIENTS, API_KEY } from '../../utils/constants';
import BingLogo from '../../Icons/bing.svg';

const SEO_AUDIT_API =
  process.env.REACT_APP_SEO_AUDIT_API ||
  'https://iw0nmalze1.execute-api.us-east-2.amazonaws.com/dev';

const corePlatforms = [
  {
    id: 'google',
    name: 'Google Business',
    icon: '🔍',
    connected: false,
    status: 'not_audited',
    lastSync: null,
    liveData: null,
    discrepancies: null,
    externalUrl: null
  },
  {
    id: 'foursquare',
    name: 'Foursquare',
    icon: '📍',
    connected: false,
    status: 'not_audited',
    lastSync: null,
    liveData: null,
    discrepancies: null,
    externalUrl: null
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: '📘',
    connected: false,
    status: 'not_audited',
    lastSync: null,
    liveData: null,
    discrepancies: null,
    externalUrl: null
  },
  {
    id: 'bing',
    name: 'Bing Places',
    icon: '🌐',
    connected: false,
    status: 'not_audited',
    lastSync: null,
    liveData: null,
    discrepancies: null,
    externalUrl: null
  }
];

const ListingManagement = () => {
  const extractApiErrorMessage = (err, fallback) =>
    err?.response?.data?.details ||
    err?.response?.data?.error_description ||
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    fallback;

  const { selectedClient } = useSelector(selectedClientSelector);
  const { sidebarOpen, setSidebarOpen } = useSidebar();
  const [activeTab, setActiveTab] = useState('business-info');

  const [platforms, setPlatforms] = useState(corePlatforms);
  const [auditSummary, setAuditSummary] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [foursquareConnected, setFoursquareConnected] = useState(false);
  const [foursquareConnecting, setFoursquareConnecting] = useState(false);
  const [facebookPageName, setFacebookPageName] = useState(null);
  const [facebookConnecting, setFacebookConnecting] = useState(false);
  const [foursquareVenueName, setFoursquareVenueName] = useState(null);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleLocationName, setGoogleLocationName] = useState(null);
  const [bingConnected, setBingConnected] = useState(false);
  const [bingAccountName, setBingAccountName] = useState(null);

  const reactSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '40px',
      borderRadius: 'calc(var(--radius) - 2px)',
      border: '1px solid hsl(var(--border))',
      boxShadow: 'none',
      backgroundColor: 'transparent',
      '&:hover': {
        borderColor: 'hsl(var(--ring))'
      }
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 12px'
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      color: 'hsl(var(--foreground))'
    }),
    singleValue: (base) => ({
      ...base,
      color: 'hsl(var(--foreground))'
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: 'hsl(var(--popover))',
      border: '1px solid hsl(var(--border))',
      borderRadius: 'calc(var(--radius) - 2px)',
      boxShadow:
        '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      zIndex: 50
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? 'hsl(var(--accent))' : 'transparent',
      color: state.isFocused
        ? 'hsl(var(--accent-foreground))'
        : 'hsl(var(--foreground))',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: 'hsl(var(--accent))'
      }
    })
  };

  const [profile, setProfile] = useState({
    businessName: '',
    phone: '',
    address: '',
    city: '',
    state: 'FL',
    zip: '',
    country: 'US',
    website: '',
    email: '',
    description: ''
  });

  const [loading, setLoading] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  // ── Foursquare OAuth Connect Flow ──
  const handleConnectFoursquare = async () => {
    if (!selectedClient?.id) {
      Swal.fire('Error', 'Please select a client first.', 'error');
      return;
    }
    try {
      setFoursquareConnecting(true);

      // 1. Get Client ID from backend
      const config = await getFoursquareConfig();
      const clientId = config.clientId;

      // 2. Build OAuth URL
      const redirectUri = `${window.location.origin}/foursquare-callback`;
      const authUrl = `https://foursquare.com/oauth2/authenticate?client_id=${clientId}&response_type=code&redirect_uri=${encodeURIComponent(
        redirectUri
      )}`;

      // 3. Open popup
      const popup = window.open(
        authUrl,
        'FoursquareOAuth',
        'width=600,height=700'
      );

      // 4. Listen for the callback
      const handleMessage = async (event) => {
        if (event.data?.type !== 'FOURSQUARE_AUTH_CALLBACK') return;
        window.removeEventListener('message', handleMessage);
        if (popup) popup.close();

        const { code } = event.data;
        if (!code) {
          Swal.fire('Error', 'No authorization code received.', 'error');
          setFoursquareConnecting(false);
          return;
        }

        try {
          // 5. Exchange code for token + get venues
          const result = await exchangeFoursquareCode(code, redirectUri);

          if (result.venues && result.venues.length > 0) {
            // Show venue selector
            const venueOptions = {};
            result.venues.forEach((v) => {
              venueOptions[v.id] = `${v.name} — ${v.address || 'No address'}`;
            });

            const { value: selectedVenueId } = await Swal.fire({
              title: 'Select Your Foursquare Venue',
              input: 'select',
              inputOptions: venueOptions,
              inputPlaceholder: 'Select a venue',
              showCancelButton: true,
              confirmButtonText: 'Connect'
            });

            if (selectedVenueId) {
              const selectedVenue = result.venues.find(
                (v) => v.id === selectedVenueId
              );
              await saveFoursquareVenue(
                selectedClient.id,
                selectedVenueId,
                selectedVenue?.name || '',
                selectedVenue?.address || '',
                result.accessToken
              );

              setFoursquareConnected(true);
              Swal.fire({
                icon: 'success',
                title: 'Connected!',
                text: `Foursquare venue "${selectedVenue?.name}" connected.`,
                timer: 3000,
                showConfirmButton: false
              });
            }
          } else {
            // No managed venues — ask user to enter venue search
            const { value: searchName } = await Swal.fire({
              title: 'No Managed Venues Found',
              text: 'Enter your business name to search Foursquare:',
              input: 'text',
              inputPlaceholder: 'Business name',
              showCancelButton: true
            });
            if (searchName) {
              Swal.fire(
                'Info',
                `We\'ll search for "${searchName}" on Foursquare during the audit. Your token has been saved.`,
                'info'
              );
            }
          }
        } catch (err) {
          console.error('Foursquare exchange error:', err);
          Swal.fire('Error', 'Failed to connect Foursquare account.', 'error');
        }
        setFoursquareConnecting(false);
      };

      window.addEventListener('message', handleMessage);

      // Safety timeout
      setTimeout(() => {
        window.removeEventListener('message', handleMessage);
        setFoursquareConnecting(false);
      }, 120000);
    } catch (err) {
      console.error('Foursquare connect error:', err);
      Swal.fire('Error', 'Failed to start Foursquare connection.', 'error');
      setFoursquareConnecting(false);
    }
  };

  // ── Facebook Connect Flow (display source is Social Manager) ──
  const handleConnectFacebook = async () => {
    if (!selectedClient?.id) {
      Swal.fire('Error', 'Please select a client first.', 'error');
      return;
    }

    try {
      setFacebookConnecting(true);
      const clientRes = await axios.get(`${API_CLIENTS}/${selectedClient.id}`, {
        headers: { 'X-API-KEY': API_KEY }
      });
      const maybeClient =
        clientRes?.data?.data ||
        clientRes?.data?.client ||
        clientRes?.data ||
        {};
      const social =
        maybeClient?.socialnetworks || selectedClient?.socialnetworks || {};

      const socialFacebook = social?.facebook;
      const resolvedName =
        (socialFacebook && typeof socialFacebook === 'object'
          ? socialFacebook.accountName ||
            socialFacebook.pageName ||
            socialFacebook.name ||
            socialFacebook.accountEmail ||
            ''
          : '') ||
        social?.facebookPage ||
        null;

      if (resolvedName) {
        setFacebookPageName(resolvedName);

        Swal.fire({
          icon: 'success',
          title: 'Connected!',
          text: `Facebook page "${resolvedName}" linked from Social Manager.`,
          timer: 2500,
          showConfirmButton: false
        });
      } else {
        Swal.fire(
          'Not Connected',
          'No Facebook page found in Social Manager. Please connect Facebook from Social Manager first.',
          'warning'
        );
      }
    } catch (err) {
      console.error('Facebook connect error:', err);
      Swal.fire(
        'Error',
        extractApiErrorMessage(err, 'Failed to fetch Facebook connection.'),
        'error'
      );
    } finally {
      setFacebookConnecting(false);
    }
  };

  useEffect(() => {
    if (selectedClient?.id) {
      loadProfileData(selectedClient.id);
      loadAuditResults(selectedClient.id);
      loadConnectedPlatforms(selectedClient.id);
    }
  }, [selectedClient]);

  // Load connected platform names from Social Manager for Google/Bing/Facebook,
  // and from ReviewSettings only for Foursquare.
  const loadConnectedPlatforms = async (locationId) => {
    let latestClientSocial = selectedClient?.socialnetworks || {};

    try {
      if (API_CLIENTS && API_KEY) {
        const clientRes = await axios.get(`${API_CLIENTS}/${locationId}`, {
          headers: { 'X-API-KEY': API_KEY }
        });

        const maybeClient =
          clientRes?.data?.data ||
          clientRes?.data?.client ||
          clientRes?.data ||
          {};

        if (maybeClient?.socialnetworks) {
          latestClientSocial = maybeClient.socialnetworks;
        }
      }
    } catch (err) {
      console.warn('Could not fetch latest client social networks:', err);
    }

    const socialGoogleName = latestClientSocial?.googleMyBusinessLocationName
      ? String(latestClientSocial.googleMyBusinessLocationName).trim()
      : '';

    setGoogleLocationName(socialGoogleName || null);
    setGoogleConnected(!!socialGoogleName);

    const socialBing = latestClientSocial?.bing;
    const socialBingName =
      socialBing && typeof socialBing === 'object'
        ? socialBing.accountName || socialBing.accountEmail || ''
        : '';
    setBingConnected(!!socialBing);
    setBingAccountName(socialBingName || null);

    const socialFacebook = latestClientSocial?.facebook;
    const socialFacebookName =
      (socialFacebook && typeof socialFacebook === 'object'
        ? socialFacebook.accountName ||
          socialFacebook.pageName ||
          socialFacebook.name ||
          socialFacebook.accountEmail ||
          ''
        : '') ||
      latestClientSocial?.facebookPage ||
      null;
    setFacebookPageName(socialFacebookName);

    try {
      const res = await axios.get(`${SEO_AUDIT_API}/reviews/settings`, {
        params: { locationId }
      });
      const settings = res.data?.settings || res.data || {};
      // Foursquare venue name
      if (settings?.listingPlatforms?.foursquare?.venueName) {
        setFoursquareVenueName(settings.listingPlatforms.foursquare.venueName);
        setFoursquareConnected(true);
      }
    } catch (err) {
      console.warn('Could not load connected platforms:', err);
    }
  };

  const loadAuditResults = async (locationId) => {
    try {
      const res = await getListingAuditResults(locationId);
      if (res.summary) {
        setAuditSummary(res.summary);
      }
      if (res.citations && res.citations.length > 0) {
        const safeFormatDate = (dateString) => {
          if (!dateString) return 'Recently';
          const d = new Date(dateString);
          return isNaN(d.getTime()) ? 'Recently' : d.toLocaleString();
        };

        const updatedPlatforms = corePlatforms.map((p) => {
          const found = res.citations.find((c) => c.directoryId === p.id);
          if (found) {
            return {
              ...p,
              status: found.status.toLowerCase(),
              connected: found.status !== 'MISSING',
              lastSync: safeFormatDate(found.lastAuditDate),
              liveData: found.liveData || null,
              discrepancies: found.discrepancies || null,
              externalUrl: found.externalUrl || null,
              error: found.error || null
            };
          }
          return p;
        });
        setPlatforms(updatedPlatforms);
      }
    } catch (err) {
      console.warn('Could not load audit results', err);
    }
  };

  const handleTriggerAudit = async () => {
    if (!selectedClient?.id) {
      Swal.fire('Error', 'No location selected', 'error');
      return;
    }
    if (!profile.businessName) {
      Swal.fire(
        'Missing Info',
        'Please fill in your Business Name and save before running an audit.',
        'warning'
      );
      return;
    }
    try {
      setAuditLoading(true);
      const result = await triggerListingAudit(selectedClient.id);

      if (result.citations) {
        // Directly update UI from the response instead of a delayed reload
        const safeFormatDate = (dateString) => {
          if (!dateString) return 'Just now';
          const d = new Date(dateString);
          return isNaN(d.getTime()) ? 'Just now' : d.toLocaleString();
        };

        const updatedPlatforms = corePlatforms.map((p) => {
          const found = result.citations.find((c) => c.directoryId === p.id);
          if (found) {
            return {
              ...p,
              status: found.status.toLowerCase(),
              connected: found.status !== 'MISSING',
              lastSync: safeFormatDate(found.lastAuditDate),
              liveData: found.liveData || null,
              discrepancies: found.discrepancies || null,
              externalUrl: found.externalUrl || null,
              error: found.error || null
            };
          }
          return p;
        });
        setPlatforms(updatedPlatforms);

        // Update summary
        if (result.summary) {
          const totalDirectories = 3;
          setAuditSummary({
            healthScore: Math.round(
              ((result.summary.live || 0) / totalDirectories) * 100
            ),
            totalCitations: totalDirectories,
            liveCitations: result.summary.live || 0,
            missingCitations: result.summary.missing || 0,
            issuesDetected:
              (result.summary.inaccurate || 0) + (result.summary.errors || 0)
          });
        }
      }

      Swal.fire({
        icon: 'success',
        title: 'Audit Complete!',
        text: `Google: ${
          result.citations?.find((c) => c.directoryId === 'google')?.status ||
          'N/A'
        }, Bing: ${
          result.citations?.find((c) => c.directoryId === 'bing')?.status ||
          'N/A'
        }, Facebook: ${
          result.citations?.find((c) => c.directoryId === 'facebook')?.status ||
          'N/A'
        }`,
        timer: 5000,
        showConfirmButton: true
      });
    } catch (err) {
      console.error('Audit error:', err);
      Swal.fire(
        'Error',
        err?.response?.data?.error || 'Failed to run audit',
        'error'
      );
    } finally {
      setAuditLoading(false);
    }
  };

  const handleTriggerSync = async () => {
    if (!selectedClient?.id) {
      Swal.fire('Error', 'No location selected', 'error');
      return;
    }
    try {
      setSyncLoading(true);
      const result = await triggerListingSync(selectedClient.id);
      const fbResult = result.syncResults?.facebook;
      const googleResult = result.syncResults?.google;
      const bingResult = result.syncResults?.bing;

      Swal.fire({
        icon: fbResult?.success ? 'success' : 'info',
        title: 'Sync Results',
        html: `
          <div style="text-align:left; font-size:14px;">
            <p><strong>Facebook:</strong> ${
              fbResult?.success
                ? '✅ Synced'
                : fbResult?.error || '⚠️ Not configured'
            }</p>
            <p><strong>Google:</strong> ${
              googleResult?.success
                ? '✅ Synced'
                : googleResult?.error ||
                  googleResult?.note ||
                  '⚠️ Not configured'
            }</p>
            <p><strong>Bing:</strong> ${
              bingResult?.success
                ? '✅ Synced'
                : bingResult?.error ||
                  bingResult?.note ||
                  '⚠️ Manual update required'
            }</p>
          </div>
        `,
        showConfirmButton: true
      });
    } catch (err) {
      console.error('Sync error:', err);
      Swal.fire('Error', 'Failed to trigger sync', 'error');
    } finally {
      setSyncLoading(false);
    }
  };

  const loadProfileData = async (locationId) => {
    try {
      setLoading(true);
      const res = await getListingProfile(locationId);
      if (res.profile) {
        setProfile((prev) => ({ ...prev, ...res.profile }));
      }
    } catch (err) {
      console.warn('Could not load listing profile, probably new', err);
    } finally {
      setLoading(false);
    }
  };

  const handleHourChange = (day, field, value) => {
    setProfile((prev) => {
      const newHours = { ...prev.hours };
      if (!newHours[day]) {
        newHours[day] = {
          isClosed: day === 'Sunday',
          open: '09:00',
          close: '17:00'
        };
      }
      newHours[day][field] = value;
      return { ...prev, hours: newHours };
    });
  };

  const handleAddSecondaryCategory = (cat) => {
    if (!cat) return;
    setProfile((prev) => {
      const cats = prev.secondaryCategories || [];
      if (!cats.includes(cat)) {
        return { ...prev, secondaryCategories: [...cats, cat] };
      }
      return prev;
    });
  };

  const handleRemoveSecondaryCategory = (cat) => {
    setProfile((prev) => {
      const cats = prev.secondaryCategories || [];
      return { ...prev, secondaryCategories: cats.filter((c) => c !== cat) };
    });
  };

  const [newCat, setNewCat] = useState('');

  const handleInputChange = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!selectedClient?.id) {
      Swal.fire('Error', 'No location selected', 'error');
      return;
    }
    try {
      setLoading(true);
      await updateListingProfile(selectedClient.id, profile);
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Business profile saved successfully.',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Save error', err);
      Swal.fire('Error', 'Failed to save business profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'live':
        return (
          <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100">
            <Check className="h-3 w-3 mr-1" /> Live
          </Badge>
        );
      case 'inaccurate':
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100">
            <AlertTriangle className="h-3 w-3 mr-1" /> Inaccurate
          </Badge>
        );
      case 'missing':
        return (
          <Badge className="bg-red-100 text-red-700 border-red-200 hover:bg-red-100">
            <X className="h-3 w-3 mr-1" /> Missing
          </Badge>
        );
      case 'error':
        return (
          <Badge className="bg-red-100 text-red-700 border-red-200 hover:bg-red-100">
            <AlertCircle className="h-3 w-3 mr-1" /> Error
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100">
            <RefreshCw className="h-3 w-3 mr-1" /> Pending
          </Badge>
        );
      case 'not_audited':
      default:
        return (
          <Badge variant="outline" className="text-muted-foreground">
            Not Audited
          </Badge>
        );
    }
  };

  const shouldHideNameDiscrepancy = (discrepancy) => {
    const text = String(discrepancy || '').toLowerCase();
    return text.includes('businessname:') || text.startsWith('name:');
  };

  const shouldHideLiveDataField = (key) => {
    return String(key || '').toLowerCase() === 'name';
  };

  return (
    <>
      {/* Top Bar for Mobile Menu Toggle */}
      <div className="lg:hidden flex items-center p-4 bg-card border-b">
        {!sidebarOpen && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}>
            <Menu className="h-5 w-5 text-primary" />
          </Button>
        )}
        <span className="ml-3 font-semibold">Listing Management</span>
      </div>

      <main className="p-6 lg:p-10 space-y-8 bg-muted/30 min-h-screen">
        {/* Page Title */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-2">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
              Listing Management{' '}
              {selectedClient?.clientName
                ? ` - ${selectedClient.clientName}`
                : ''}
            </h1>
            <p className="text-base text-muted-foreground mt-1">
              Manage your business listings across Google, Bing, and Facebook
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="gap-2 h-12 px-6 rounded-xl text-base"
              onClick={handleTriggerAudit}
              disabled={auditLoading || syncLoading}>
              <Search
                className={`h-4 w-4 ${auditLoading ? 'animate-bounce' : ''}`}
              />
              {auditLoading ? 'Auditing...' : 'Run Audit'}
            </Button>
            <Button
              variant="outline"
              className="gap-2 h-12 px-6 rounded-xl text-base"
              onClick={handleTriggerSync}
              disabled={auditLoading || syncLoading}>
              <Upload
                className={`h-4 w-4 ${syncLoading ? 'animate-spin' : ''}`}
              />
              {syncLoading ? 'Syncing...' : 'Sync to Platforms'}
            </Button>
            <Button
              size="lg"
              onClick={handleSave}
              disabled={loading}
              className="bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-8 text-base font-semibold rounded-xl shadow hover:shadow-md transition-all">
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Listing Tracker */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  Listing Tracker{' '}
                  {auditSummary && `(Health: ${auditSummary.healthScore}%)`}
                </CardTitle>
                <CardDescription>
                  Real-time status of your business listings across connected
                  platforms
                </CardDescription>
              </div>
              {auditSummary && (
                <div className="flex gap-4 text-sm">
                  <div className="flex flex-col items-center">
                    <span className="font-bold text-green-600">
                      {auditSummary.liveCitations}
                    </span>
                    <span>Live</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="font-bold text-red-600">
                      {auditSummary.missingCitations}
                    </span>
                    <span>Missing</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="font-bold text-amber-600">
                      {auditSummary.issuesDetected}
                    </span>
                    <span>Issues</span>
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {/* Core Platforms */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {platforms
                  .filter((platform) => platform.id !== 'foursquare')
                  .map((platform) => (
                    <div
                      key={platform.id}
                      className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                        selectedPlatform?.id === platform.id
                          ? 'border-primary ring-2 ring-primary/20'
                          : platform.status !== 'not_audited'
                          ? 'border-border bg-background'
                          : 'border-dashed border-muted-foreground/30 bg-muted/50'
                      }`}
                      onClick={() =>
                        setSelectedPlatform(
                          selectedPlatform?.id === platform.id ? null : platform
                        )
                      }>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {platform.id === 'bing' ||
                          platform.id === 'google' ||
                          platform.id === 'facebook' ? (
                            <span className="h-12 w-12 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center overflow-hidden">
                              {platform.id === 'facebook' ? (
                                <span className="h-12 w-12 rounded-full bg-blue-600 flex items-center justify-center shadow-lg">
                                  <Facebook className="h-6 w-6 text-white" />
                                </span>
                              ) : platform.id === 'google' ? (
                                <span className="h-12 w-12 rounded-full bg-white flex items-center justify-center shadow-lg">
                                  <svg className="h-6 w-6" viewBox="0 0 24 24">
                                    <path
                                      fill="#4285F4"
                                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                    />
                                    <path
                                      fill="#34A853"
                                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                    />
                                    <path
                                      fill="#FBBC05"
                                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                    />
                                    <path
                                      fill="#EA4335"
                                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                    />
                                  </svg>
                                </span>
                              ) : (
                                <img
                                  src={BingLogo}
                                  alt={platform.name}
                                  className="h-7 w-7 object-contain"
                                />
                              )}
                            </span>
                          ) : (
                            <span className="text-2xl">{platform.icon}</span>
                          )}
                          <span className="font-medium text-sm">
                            {platform.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {platform.externalUrl && (
                            <a
                              href={platform.externalUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6">
                                <ExternalLink className="h-3 w-3" />
                              </Button>
                            </a>
                          )}
                          {/* Connect button for Foursquare */}
                          {platform.id === 'foursquare' &&
                            !foursquareConnected && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs px-3"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleConnectFoursquare();
                                }}
                                disabled={foursquareConnecting}>
                                {foursquareConnecting
                                  ? 'Connecting...'
                                  : 'Connect'}
                              </Button>
                            )}
                          {platform.id === 'foursquare' &&
                            foursquareConnected && (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                <Check className="h-3 w-3 mr-1" /> Connected
                              </Badge>
                            )}
                          {/* Facebook Connect button removed from card */}
                          {/* Facebook — show Connected if page is linked */}
                          {platform.id === 'facebook' && facebookPageName && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">
                              <Check className="h-3 w-3 mr-1" /> Connected
                            </Badge>
                          )}
                          {platform.id === 'google' && googleConnected && (
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              <Check className="h-3 w-3 mr-1" /> Connected
                            </Badge>
                          )}
                          {platform.id === 'bing' && !bingConnected && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">
                              <X className="h-3 w-3 mr-1" /> Disconnected
                            </Badge>
                          )}
                          {platform.id === 'bing' && bingConnected && (
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              <Check className="h-3 w-3 mr-1" /> Connected
                            </Badge>
                          )}
                        </div>
                      </div>
                      {/* Show connected account name */}
                      {platform.id === 'facebook' && facebookPageName && (
                        <p
                          className="text-xs text-muted-foreground mb-1 truncate"
                          title={facebookPageName}>
                          📄 {facebookPageName}
                        </p>
                      )}
                      {platform.id === 'foursquare' && foursquareVenueName && (
                        <p
                          className="text-xs text-muted-foreground mb-1 truncate"
                          title={foursquareVenueName}>
                          📍 {foursquareVenueName}
                        </p>
                      )}
                      {platform.id === 'google' && googleLocationName && (
                        <p
                          className="text-xs text-muted-foreground mb-1 truncate"
                          title={googleLocationName}>
                          🏢 {googleLocationName}
                        </p>
                      )}
                      {platform.id === 'bing' && bingAccountName && (
                        <p
                          className="text-xs text-muted-foreground mb-1 truncate"
                          title={bingAccountName}>
                          👤 {bingAccountName}
                        </p>
                      )}
                      <div className="flex items-center justify-between">
                        {getStatusBadge(platform.status)}
                        {platform.lastSync &&
                          platform.status !== 'not_audited' && (
                            <span className="text-xs text-muted-foreground">
                              {platform.lastSync}
                            </span>
                          )}
                      </div>
                      {platform.error && (
                        <p className="text-xs text-red-500 mt-2 truncate">
                          {platform.error}
                        </p>
                      )}
                    </div>
                  ))}
              </div>

              {/* Detail Panel for Selected Platform */}
              {selectedPlatform && (
                <div className="p-4 rounded-lg border border-primary/20 bg-primary/5">
                  <h3 className="font-semibold text-lg mb-3">
                    {selectedPlatform.icon} {selectedPlatform.name} — Details
                  </h3>

                  {selectedPlatform.discrepancies &&
                    selectedPlatform.discrepancies.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-amber-700 mb-2">
                          ⚠️ Discrepancies Found:
                        </p>
                        <ul className="list-disc list-inside text-sm space-y-1">
                          {selectedPlatform.discrepancies.map((d, i) => (
                            <li key={i} className="text-amber-600">
                              {d}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                  {selectedPlatform.liveData && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2">
                        Live Data from {selectedPlatform.name}:
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        {Object.entries(selectedPlatform.liveData).map(
                          ([key, value]) =>
                            value && (
                              <div key={key} className="flex gap-2">
                                <span className="font-medium text-muted-foreground capitalize">
                                  {key}:
                                </span>
                                <span className="text-foreground truncate">
                                  {typeof value === 'object'
                                    ? JSON.stringify(value)
                                    : String(value)}
                                </span>
                              </div>
                            )
                        )}
                      </div>
                    </div>
                  )}

                  {selectedPlatform.status === 'missing' && (
                    <p className="text-sm text-red-500">
                      {selectedPlatform.error ||
                        `This business was not found on ${selectedPlatform.name}. You may need to create a listing manually.`}
                    </p>
                  )}

                  {selectedPlatform.status === 'not_audited' && (
                    <p className="text-sm text-muted-foreground">
                      Click "Run Audit" to check your listing status on{' '}
                      {selectedPlatform.name}.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Business Information Form */}
          <Card>
            <CardHeader>
              <CardTitle>Local Listing / Citation Builder</CardTitle>
              <CardDescription>
                Update your business information once and Adler Suite will push
                the data to all connected listings and keep them synced.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-6 flex-wrap h-auto">
                  <TabsTrigger
                    value="business-info"
                    className="w-full sm:w-auto">
                    Business Info
                  </TabsTrigger>
                  <TabsTrigger value="hours" className="w-full sm:w-auto">
                    Hours
                  </TabsTrigger>
                  <TabsTrigger value="categories" className="w-full sm:w-auto">
                    Categories
                  </TabsTrigger>
                  <TabsTrigger value="attributes" className="w-full sm:w-auto">
                    Attributes
                  </TabsTrigger>
                </TabsList>

                <TabsContent tabValue="business-info" className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="businessName">Business Name</Label>
                      <Input
                        id="businessName"
                        placeholder="e.g. Acme Corp"
                        value={profile.businessName}
                        onChange={(e) =>
                          handleInputChange('businessName', e.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="phone"
                          placeholder="(555) 123-4567"
                          className="pl-10"
                          value={profile.phone}
                          onChange={(e) =>
                            handleInputChange('phone', e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="address">Street Address</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="address"
                          placeholder="123 Main St"
                          className="pl-10"
                          value={profile.address}
                          onChange={(e) =>
                            handleInputChange('address', e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        placeholder="Miami"
                        value={profile.city}
                        onChange={(e) =>
                          handleInputChange('city', e.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State / Province</Label>
                      <ReactSelect
                        id="state"
                        value={
                          usStatesAndCaProvinces.find(
                            (s) => s.value === profile.state
                          ) || null
                        }
                        onChange={(option) =>
                          handleInputChange('state', option ? option.value : '')
                        }
                        options={usStatesAndCaProvinces}
                        styles={reactSelectStyles}
                        placeholder="Search state..."
                        isClearable
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zip">ZIP Code</Label>
                      <Input
                        id="zip"
                        placeholder="33101"
                        value={profile.zip}
                        onChange={(e) =>
                          handleInputChange('zip', e.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">Country</Label>
                      <ReactSelect
                        id="country"
                        value={
                          countries.find((c) => c.value === profile.country) ||
                          null
                        }
                        onChange={(option) =>
                          handleInputChange(
                            'country',
                            option ? option.value : ''
                          )
                        }
                        options={countries}
                        styles={reactSelectStyles}
                        placeholder="Search country..."
                        isClearable
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website">Website</Label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="website"
                          placeholder="https://example.com"
                          className="pl-10"
                          value={profile.website}
                          onChange={(e) =>
                            handleInputChange('website', e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="contact@example.com"
                        value={profile.email}
                        onChange={(e) =>
                          handleInputChange('email', e.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="description">Business Description</Label>
                      <Textarea
                        id="description"
                        placeholder="Describe your business..."
                        className="min-h-[100px]"
                        value={profile.description}
                        onChange={(e) =>
                          handleInputChange('description', e.target.value)
                        }
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent tabValue="hours" className="space-y-6">
                  <div className="space-y-4">
                    {[
                      'Monday',
                      'Tuesday',
                      'Wednesday',
                      'Thursday',
                      'Friday',
                      'Saturday',
                      'Sunday'
                    ].map((day) => {
                      const dayHours =
                        profile.hours && profile.hours[day]
                          ? profile.hours[day]
                          : {
                              isClosed: day === 'Sunday',
                              open: '09:00',
                              close: '17:00'
                            };
                      return (
                        <div
                          key={day}
                          className="flex flex-wrap sm:flex-nowrap items-center gap-4 p-3 rounded-lg border border-border">
                          <div className="w-28 flex-shrink-0">
                            <span className="font-medium text-sm">{day}</span>
                          </div>
                          <Switch
                            checked={!dayHours.isClosed}
                            onCheckedChange={(checked) =>
                              handleHourChange(day, 'isClosed', !checked)
                            }
                          />
                          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                            <Select
                              value={dayHours.open}
                              onValueChange={(val) =>
                                handleHourChange(day, 'open', val)
                              }
                              disabled={dayHours.isClosed}>
                              <SelectTrigger className="w-full sm:w-28">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from({ length: 24 }, (_, i) => (
                                  <SelectItem
                                    key={i}
                                    value={`${i
                                      .toString()
                                      .padStart(2, '0')}:00`}>
                                    {`${i.toString().padStart(2, '0')}:00`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <span className="text-muted-foreground">to</span>
                            <Select
                              value={dayHours.close}
                              onValueChange={(val) =>
                                handleHourChange(day, 'close', val)
                              }
                              disabled={dayHours.isClosed}>
                              <SelectTrigger className="w-full sm:w-28">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from({ length: 24 }, (_, i) => (
                                  <SelectItem
                                    key={i}
                                    value={`${i
                                      .toString()
                                      .padStart(2, '0')}:00`}>
                                    {`${i.toString().padStart(2, '0')}:00`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {dayHours.isClosed && (
                              <span className="text-xs text-muted-foreground italic">
                                Closed
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </TabsContent>

                <TabsContent tabValue="categories" className="space-y-6">
                  <div className="space-y-4 max-w-xl">
                    <div className="space-y-2">
                      <Label>Primary Category</Label>
                      <Select
                        value={profile.primaryCategory || 'tech'}
                        onValueChange={(val) =>
                          handleInputChange('primaryCategory', val)
                        }>
                        <SelectTrigger>
                          <SelectValue placeholder="Select primary category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tech">
                            Technology Company
                          </SelectItem>
                          <SelectItem value="software">
                            Software Company
                          </SelectItem>
                          <SelectItem value="consulting">
                            IT Consulting
                          </SelectItem>
                          <SelectItem value="ai">
                            Artificial Intelligence Company
                          </SelectItem>
                          <SelectItem value="seo">SEO Services</SelectItem>
                          <SelectItem value="marketing">
                            Marketing Agency
                          </SelectItem>
                          <SelectItem value="restaurant">Restaurant</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Secondary Categories</Label>
                      <div className="flex flex-wrap gap-2">
                        {(profile.secondaryCategories || []).map((cat) => (
                          <Badge
                            key={cat}
                            className="bg-primary text-primary-foreground px-3 py-1 hover:bg-primary/90">
                            {cat}{' '}
                            <X
                              className="h-3 w-3 ml-2 cursor-pointer"
                              onClick={() => handleRemoveSecondaryCategory(cat)}
                            />
                          </Badge>
                        ))}
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Input
                          placeholder="Add a category..."
                          className="flex-1"
                          value={newCat}
                          onChange={(e) => setNewCat(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddSecondaryCategory(newCat);
                              setNewCat('');
                            }
                          }}
                        />
                        <Button
                          variant="outline"
                          onClick={() => {
                            handleAddSecondaryCategory(newCat);
                            setNewCat('');
                          }}>
                          Add
                        </Button>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent tabValue="attributes" className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                      <div>
                        <p className="font-medium text-sm">
                          Wheelchair Accessible
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Business is wheelchair accessible
                        </p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                    <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                      <div>
                        <p className="font-medium text-sm">Free WiFi</p>
                        <p className="text-xs text-muted-foreground">
                          Free WiFi available for customers
                        </p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                    <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                      <div>
                        <p className="font-medium text-sm">
                          Accepts Credit Cards
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Payment via credit cards accepted
                        </p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                    <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                      <div>
                        <p className="font-medium text-sm">Parking Available</p>
                        <p className="text-xs text-muted-foreground">
                          On-site parking available
                        </p>
                      </div>
                      <Switch defaultChecked />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Sync Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Sync Settings</CardTitle>
              <CardDescription>
                Configure how Adler Suite syncs your listing data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 max-w-2xl">
              <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                <div>
                  <p className="font-medium text-sm">Auto-Sync</p>
                  <p className="text-xs text-muted-foreground">
                    Automatically sync changes to all platforms
                  </p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                <div>
                  <p className="font-medium text-sm">Sync Notifications</p>
                  <p className="text-xs text-muted-foreground">
                    Get notified when sync completes or fails
                  </p>
                </div>
                <Switch defaultChecked />
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </>
  );
};

export default ListingManagement;
